/**
 * GRC Copilot Sidebar (CABINET)
 * Persistent AI companion — re-housed into the floating CompanionWindow
 * (COPILOT_MASTER_PLAN Phase 1 / subsystem A + E + H + I).
 *
 * The existing cabinet chat plumbing (copilotApi.chatStream, credit guard,
 * quick actions, AgentModePanel, CreateWithAi, MeetingActions, LLM selectors)
 * is UNCHANGED — it is simply mounted inside the draggable / dockable /
 * minimizable CompanionWindow with an added TaskTray tab and an
 * Assistant/Agent mode toggle. Window state persists to
 * localStorage['sgrc-adminclient-companion-window'].
 */

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Sparkles,
  X,
  Send,
  Bot,
  User,
  Loader2,
  Copy,
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  ListTodo,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { copilotApi, type CopilotMessage } from '@/services/copilot.service'
import { LlmModeSelector, useLlmCreditGuard } from '@/components/copilot/LlmModeSelector'
import { LlmTierSelector } from '@/components/copilot/LlmTierSelector'
import { AgentModePanel } from '@/components/copilot/AgentModePanel'
import { CreateWithAi } from '@/components/copilot/CreateWithAi'
import { MeetingActions } from '@/components/copilot/MeetingActions'
import { CompanionWindow } from '@/components/companion/CompanionWindow'
import { TaskTray, useCompanionTasks } from '@/components/companion/TaskTray'
import { useCompanionWindowStore } from '@/stores/useCompanionWindowStore'

// ── Props ────────────────────────────────────────────────────────────────────

export interface GrcCopilotSidebarProps {
  moduleContext: string
  pageContext: string
  entityData?: Record<string, unknown>
}

// ── Quick Actions ────────────────────────────────────────────────────────────

const QUICK_ACTIONS = [
  { label: 'Résumer', prompt: 'Résume les informations clés de cette page.' },
  { label: 'Analyser', prompt: 'Analyse les données affichées et identifie les points importants.' },
  { label: 'Recommander', prompt: 'Quelles actions recommandes-tu en priorité ?' },
  { label: 'Expliquer', prompt: 'Explique en détail le contexte et les implications.' },
]

// ── Markdown renderer (lightweight) ──────────────────────────────────────────

function renderMarkdown(text: string): React.ReactNode {
  const lines = text.split('\n')
  const elements: React.ReactNode[] = []

  lines.forEach((line, i) => {
    if (line.startsWith('### ')) {
      elements.push(<h4 key={i} className="font-semibold text-sm mt-2 mb-1">{line.slice(4)}</h4>)
    } else if (line.startsWith('## ')) {
      elements.push(<h3 key={i} className="font-bold text-sm mt-3 mb-1">{line.slice(3)}</h3>)
    } else if (line.startsWith('# ')) {
      elements.push(<h2 key={i} className="font-bold text-base mt-3 mb-1">{line.slice(2)}</h2>)
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      elements.push(
        <li key={i} className="ml-4 text-sm list-disc">{renderInlineMarkdown(line.slice(2))}</li>
      )
    } else if (/^\d+\.\s/.test(line)) {
      const content = line.replace(/^\d+\.\s/, '')
      elements.push(
        <li key={i} className="ml-4 text-sm list-decimal">{renderInlineMarkdown(content)}</li>
      )
    } else if (line.startsWith('```')) {
      // Skip code block delimiters
    } else if (line.trim() === '') {
      elements.push(<br key={i} />)
    } else {
      elements.push(<p key={i} className="text-sm mb-1">{renderInlineMarkdown(line)}</p>)
    }
  })

  return <>{elements}</>
}

function renderInlineMarkdown(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={i} className="bg-muted px-1 py-0.5 rounded text-xs font-mono">
          {part.slice(1, -1)}
        </code>
      )
    }
    return part
  })
}

// ── Typing Indicator ─────────────────────────────────────────────────────────

function TypingIndicator() {
  return (
    <div className="flex items-center gap-2 p-3">
      <Avatar className="h-7 w-7">
        <AvatarFallback className="bg-primary/10 text-primary">
          <Bot className="h-4 w-4" />
        </AvatarFallback>
      </Avatar>
      <div className="flex gap-1 items-center">
        <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:0ms]" />
        <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:150ms]" />
        <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:300ms]" />
      </div>
    </div>
  )
}

// ── Main Component ───────────────────────────────────────────────────────────

export function GrcCopilotSidebar({ moduleContext, pageContext, entityData }: GrcCopilotSidebarProps) {
  const { guardDialog, ensureSufficientCredit } = useLlmCreditGuard()

  // ── Companion window chrome (Phase 1 subsystem A) — persisted per user ──────
  // open/minimized/dock/position/size + Assistant-vs-Agent mode + active tab all
  // live in useCompanionWindowStore (localStorage 'sgrc-adminclient-companion-window').
  const open = useCompanionWindowStore((s) => s.open)
  const minimized = useCompanionWindowStore((s) => s.minimized)
  const activeTab = useCompanionWindowStore((s) => s.activeTab)
  const badge = useCompanionWindowStore((s) => s.badge)
  const badgeCount = useCompanionWindowStore((s) => s.badgeCount)
  const openWindow = useCompanionWindowStore((s) => s.openWindow)
  const closeWindow = useCompanionWindowStore((s) => s.closeWindow)
  const restoreWindow = useCompanionWindowStore((s) => s.restore)
  const setActiveTab = useCompanionWindowStore((s) => s.setActiveTab)

  const [messages, setMessages] = useState<CopilotMessage[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Task tray polling (subsystem E) — runs while the Companion is open OR
  // minimized so the pill badge can light up on task completion / HITL.
  const tasksQuery = useCompanionTasks(open || minimized)
  const trayTasks = tasksQuery.data?.tasks ?? []
  const traySource = tasksQuery.data?.source ?? 'none'

  // Scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages, isTyping])

  // Focus input when the window opens on the chat tab
  useEffect(() => {
    if (open && !minimized && activeTab === 'chat') {
      setTimeout(() => inputRef.current?.focus(), 300)
    }
  }, [open, minimized, activeTab])

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || isTyping) return

      // AI-credit pre-flight: prompt to switch to free OSS mode if the active
      // commercial LLM has no credit. Aborts if the user declines.
      const creditOk = await ensureSufficientCredit(5)
      if (!creditOk) return

      const userMsg: CopilotMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        content: text.trim(),
        timestamp: new Date().toISOString(),
      }

      const assistantMsgId = crypto.randomUUID()

      setMessages((prev) => [...prev, userMsg])
      setInputValue('')
      setIsTyping(true)
      setIsStreaming(true)

      // Add an empty assistant message that will be filled token-by-token
      const emptyAssistantMsg: CopilotMessage = {
        id: assistantMsgId,
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
      }
      setMessages((prev) => [...prev, emptyAssistantMsg])

      try {
        await copilotApi.chatStream(
          text.trim(),
          { moduleContext, pageContext, entityData, page_data: entityData },
          [...messages, userMsg],
          (token: string) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsgId ? { ...m, content: m.content + token } : m
              )
            )
          },
          () => {
            setIsStreaming(false)
            setIsTyping(false)
          },
          (error: string) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsgId
                  ? { ...m, content: `Erreur: ${error}` }
                  : m
              )
            )
            setIsStreaming(false)
            setIsTyping(false)
          },
        )
      } catch (error) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsgId
              ? { ...m, content: 'Désolé, une erreur est survenue. Veuillez réessayer.' }
              : m
          )
        )
        setIsStreaming(false)
        setIsTyping(false)
      }
    },
    [messages, moduleContext, pageContext, entityData, isTyping, ensureSufficientCredit]
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    sendMessage(inputValue)
  }

  const handleQuickAction = (prompt: string) => {
    sendMessage(prompt)
  }

  const handleCopyMessage = (content: string) => {
    navigator.clipboard.writeText(content)
  }

  const handleClearChat = () => {
    setMessages([])
  }

  const handleFeedback = useCallback(
    async (messageId: string, rating: 'positive' | 'negative') => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, feedback: rating } : m
        )
      )
      try {
        await copilotApi.submitFeedback(messageId, rating, { moduleContext, pageContext })
      } catch (err) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId ? { ...m, feedback: undefined } : m
          )
        )
      }
    },
    [moduleContext, pageContext]
  )

  return (
    <>
      {/* Floating launcher — shown while the Companion window is closed. */}
      {!open && !minimized && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={openWindow}
                className="group fixed bottom-6 right-6 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-primary to-blue-500 text-white shadow-lg shadow-primary/30 ring-1 ring-primary/20 transition-all hover:scale-105 hover:shadow-xl"
                aria-label="Assistant IA — GRC Copilot"
                title="Assistant IA — GRC Copilot"
                data-copilot-id="copilot-fab"
              >
                <span className="pointer-events-none absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/40 opacity-60" />
                <span className="absolute -right-0.5 -top-0.5 inline-flex h-3.5 w-3.5 animate-pulse rounded-full bg-emerald-400 ring-2 ring-background" />
                <Sparkles className="relative h-6 w-6" />
                <span className="sr-only">Ouvrir l&apos;assistant IA</span>
              </button>
            </TooltipTrigger>
            <TooltipContent side="left" className="font-medium">
              Assistant IA
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Minimized pill — compact status chip; badge lights on task completion
          or pending HITL; click restores the window. Never blocks the page. */}
      {open && minimized && (
        <div
          className="fixed bottom-6 right-6 z-[70] flex items-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 ring-1 ring-primary/20"
          data-copilot-id="companion-pill"
        >
          <button
            onClick={restoreWindow}
            className="flex h-11 items-center gap-2 rounded-l-full pl-4 pr-1 transition-transform hover:scale-[1.02]"
            aria-label="Restaurer l'assistant IA"
            data-copilot-id="companion-pill-restore"
          >
            <Sparkles className="h-4 w-4" />
            <span className="text-sm font-medium">Copilot</span>
            {(badgeCount > 0 || badge != null) && (
              <span
                data-copilot-id="companion-pill-badge"
                className={cn(
                  'inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1 text-[10px] font-bold',
                  badge === 'hitl'
                    ? 'animate-pulse bg-amber-400 text-amber-950'
                    : 'bg-emerald-400 text-emerald-950',
                )}
              >
                {badge === 'hitl' ? '!' : badgeCount > 0 ? badgeCount : '✓'}
              </span>
            )}
          </button>
          <button
            onClick={closeWindow}
            className="flex h-11 items-center rounded-r-full pl-1 pr-3 opacity-70 hover:opacity-100"
            aria-label="Fermer l'assistant IA"
            data-copilot-id="companion-pill-close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Companion window (Phase 1 subsystem A) — draggable/dockable/resizable
          chrome re-housing the existing cabinet copilot body. */}
      {open && !minimized && (
        <CompanionWindow
          headerActions={
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleClearChat} title="Nouvelle conversation">
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          }
        >
          {/* LLM provider mode (Commercial vs Open Source) */}
          <div className="shrink-0 px-4 py-2 border-b flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">Modèle IA</span>
            <LlmModeSelector />
          </div>

          {/* LLM tier + model (Premium / Équilibré / Économique / OSS) */}
          <div className="shrink-0 px-4 py-2 border-b">
            <LlmTierSelector />
          </div>

          {guardDialog}

          {/* Context Badge */}
          <div className="shrink-0 px-4 py-2 border-b bg-muted/30">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="secondary" className="text-xs">
                {moduleContext}
              </Badge>
              <Badge variant="outline" className="text-xs">
                {pageContext}
              </Badge>
            </div>
          </div>

          {/* Chat / Agent / Tasks tabs — chat+agent stay in sync with the
              header Assistant/Agent mode toggle (subsystem H). */}
          <div className="shrink-0 px-4 py-2 border-b">
            <div className="grid grid-cols-3 gap-1 rounded-lg bg-muted p-1">
              <button
                onClick={() => setActiveTab('chat')}
                className={cn(
                  'flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                  activeTab === 'chat'
                    ? 'bg-background shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
                data-copilot-id="copilot-tab-chat"
              >
                <MessageSquare className="h-3.5 w-3.5" />
                Assistant
              </button>
              <button
                onClick={() => setActiveTab('agent')}
                className={cn(
                  'flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                  activeTab === 'agent'
                    ? 'bg-background shadow-sm text-primary'
                    : 'text-muted-foreground hover:text-foreground',
                )}
                data-copilot-id="copilot-tab-agent"
              >
                <Bot className="h-3.5 w-3.5" />
                Agent
              </button>
              <button
                onClick={() => setActiveTab('tasks')}
                className={cn(
                  'relative flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                  activeTab === 'tasks'
                    ? 'bg-background shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
                data-copilot-id="copilot-tab-tasks"
              >
                <ListTodo className="h-3.5 w-3.5" />
                Tâches
                {(badgeCount > 0 || badge != null) && activeTab !== 'tasks' && (
                  <span
                    className={cn(
                      'absolute -right-0.5 -top-0.5 inline-flex h-3.5 min-w-[0.875rem] items-center justify-center rounded-full px-0.5 text-[9px] font-bold',
                      badge === 'hitl' ? 'bg-amber-400 text-amber-950' : 'bg-emerald-400 text-emerald-950',
                    )}
                    data-copilot-id="copilot-tab-tasks-badge"
                  >
                    {badge === 'hitl' ? '!' : badgeCount || ''}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* ── Agent mode: plan + act ─────────────────────────────────────── */}
          {activeTab === 'agent' && (
            <div className="flex-1 min-h-0 overflow-hidden">
              <AgentModePanel module={moduleContext} pageContext={pageContext} />
            </div>
          )}

          {/* ── Task tray (subsystem E): Companion/agentic task list ────────── */}
          {activeTab === 'tasks' && (
            <TaskTray
              tasks={trayTasks}
              source={traySource}
              isLoading={tasksQuery.isLoading || tasksQuery.isFetching}
            />
          )}

          {/* ── Chat mode: messages + quick actions + input ─────────────────── */}
          {activeTab === 'chat' && (
            <>
              {/* Messages Area */}
              <div className="flex-1 min-h-0 overflow-hidden">
                <ScrollArea className="h-full">
                  <div ref={scrollRef} className="p-4 space-y-4">
                    {messages.length === 0 && (
                      <div className="text-center py-8 space-y-3">
                        <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <Bot className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">Bienvenue dans GRC Copilot</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Posez vos questions sur {moduleContext} ou utilisez les actions rapides ci-dessous.
                          </p>
                        </div>
                      </div>
                    )}

                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        {msg.role === 'assistant' && (
                          <Avatar className="h-7 w-7 shrink-0">
                            <AvatarFallback className="bg-primary/10 text-primary">
                              <Bot className="h-4 w-4" />
                            </AvatarFallback>
                          </Avatar>
                        )}

                        <div
                          className={`group relative max-w-[85%] rounded-lg px-3 py-2 ${
                            msg.role === 'user'
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted'
                          }`}
                        >
                          {msg.role === 'user' ? (
                            <p className="text-sm">{msg.content}</p>
                          ) : (
                            <div className="prose-sm">
                              {renderMarkdown(msg.content)}
                              {isStreaming && msg.id === messages[messages.length - 1]?.id && (
                                <span className="inline-block w-2 h-4 bg-primary/70 animate-pulse ml-0.5 align-text-bottom" />
                              )}
                            </div>
                          )}

                          {msg.role === 'assistant' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="absolute -right-8 top-0 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => handleCopyMessage(msg.content)}
                              title="Copier"
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          )}

                          {/* Sources */}
                          {msg.metadata?.sources && Array.isArray(msg.metadata.sources) && msg.metadata.sources.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-border/50">
                              <p className="text-xs text-muted-foreground mb-1">Sources:</p>
                              {(msg.metadata.sources as { title: string; url?: string }[]).map((src, i) => (
                                <span key={i} className="text-xs text-primary/80 mr-2">
                                  {src.url ? (
                                    <a href={src.url} className="hover:underline">{src.title}</a>
                                  ) : (
                                    src.title
                                  )}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Feedback Buttons */}
                          {msg.role === 'assistant' && msg.content && (
                            <div className="flex items-center gap-1 mt-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className={cn("h-6 w-6 p-0", msg.feedback === 'positive' && "text-green-500")}
                                onClick={() => handleFeedback(msg.id, 'positive')}
                              >
                                <ThumbsUp className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className={cn("h-6 w-6 p-0", msg.feedback === 'negative' && "text-red-500")}
                                onClick={() => handleFeedback(msg.id, 'negative')}
                              >
                                <ThumbsDown className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </div>

                        {msg.role === 'user' && (
                          <Avatar className="h-7 w-7 shrink-0">
                            <AvatarFallback className="bg-secondary">
                              <User className="h-4 w-4" />
                            </AvatarFallback>
                          </Avatar>
                        )}
                      </div>
                    ))}

                    {isTyping && !isStreaming && <TypingIndicator />}
                  </div>
                </ScrollArea>
              </div>

              {/* Quick Actions */}
              {messages.length === 0 && (
                <div className="shrink-0 px-4 py-2 border-t bg-muted/20 space-y-2">
                  <div className="flex flex-wrap gap-1.5">
                    <CreateWithAi onLaunch={closeWindow} />
                    <MeetingActions module={moduleContext} />
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {QUICK_ACTIONS.map((action) => (
                      <Button
                        key={action.label}
                        variant="outline"
                        size="sm"
                        className="text-xs h-7 rounded-full"
                        onClick={() => handleQuickAction(action.prompt)}
                      >
                        {action.label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Input Area */}
              <div className="shrink-0 border-t p-3">
                <form onSubmit={handleSubmit} className="flex gap-2">
                  <Input
                    ref={inputRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="Posez votre question..."
                    className="flex-1 text-sm"
                    disabled={isTyping}
                  />
                  <Button
                    type="submit"
                    size="icon"
                    className="shrink-0"
                    disabled={!inputValue.trim() || isTyping}
                  >
                    {isTyping ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </form>
              </div>
            </>
          )}
        </CompanionWindow>
      )}
    </>
  )
}

export default GrcCopilotSidebar
